import { useState, useRef, useEffect } from "react";
import {
  RefreshCw, Eye, EyeOff, ArrowUp, ArrowDown, RotateCcw, Check, Bug,
  ChevronDown, ChevronRight, BarChart3, BookOpen, Download, Upload,
  TrendingUp, KeyRound, Copy, Trash2,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { ScrollArea } from "./ui/scroll-area";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Progress } from "./ui/progress";
import { THEMES } from "../lib/theme";
import {
  downloadBackup, uploadBackup,
  fetchPokecardexWatch, adoptPokecardexCards, fetchPokedex,
  fetchExtensionTokens, createExtensionToken, revealExtensionToken, revokeExtensionToken,
} from "../lib/api";
import { pokemonSpriteUrl } from "../lib/images";
import { useOfflineSync } from "../hooks/useOfflineSync";
import { clearOfflineCache, testOfflineStorage } from "../lib/offlineStore";
import { RarityBallSettings } from "./RarityBallSettings";
import { ScannerSettings } from "./ScannerSettings";

// Panneau Réglages : mise à jour des données, diagnostic, et personnalisation
// de l'affichage (blocs et sets masqués/ordonnés, périmètre de la barre de
// progression globale). Les préférences sont enregistrées côté backend, donc
// elles suivent le compte et pas le navigateur.
// Choix du Pokémon-avatar de la barre du haut (demande utilisateur du
// 2026-10-03 : « il faudrait qu'on puisse la changer »). Les noms viennent
// de la table du Pokédex (déjà construite côté serveur), les sprites de la
// même source statique que la vue Pokédex. Rien à héberger.
const AvatarPicker = ({ value, onChange }) => {
  const [names, setNames] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => {
    fetchPokedex().then(d => setNames(d.names || {})).catch(() => setNames({}));
  }, []);
  const needle = q.trim().toLowerCase();
  const matches = names
    ? Object.entries(names)
        .filter(([dex, name]) => needle ? name.toLowerCase().includes(needle) || dex === needle : false)
        .slice(0, 24)
    : [];
  return (
    <div className="rounded-lg border border-zinc-700 p-3">
      <div className="flex items-center gap-3 mb-2">
        <span className="w-10 h-10 rounded-full bg-zinc-950 border border-zinc-700 grid place-items-center overflow-hidden shrink-0">
          {value ? <img src={pokemonSpriteUrl(value)} alt="" className="w-12 h-12" style={{ imageRendering: "pixelated" }} />
                 : <span className="text-xs text-zinc-500">Aa</span>}
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-sm text-white">{value ? (names?.[String(value)] || `N° ${value}`) : "Initiale du pseudo"}</div>
          <div className="text-[11px] text-zinc-500">Un Pokémon à la place de l'initiale, dans la barre du haut.</div>
        </div>
        {value && (
          <button onClick={() => onChange(null)} className="text-xs text-zinc-400 hover:text-white shrink-0">Retirer</button>
        )}
      </div>
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Chercher un Pokémon par nom ou numéro…"
        className="search-input h-9" />
      {needle && (
        <div className="avatar-grid mt-2">
          {matches.length === 0 && <div className="col-span-full text-xs text-zinc-500 py-2">Aucun Pokémon ne correspond.</div>}
          {matches.map(([dex, name]) => (
            <button key={dex} title={`${name} (n° ${dex})`} onClick={() => { onChange(parseInt(dex, 10)); setQ(""); }}
              className={`avatar-pick ${String(value) === dex ? "on" : ""}`}>
              <img src={pokemonSpriteUrl(dex)} alt={name} loading="lazy" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Jetons dédiés à l'extension Œil de Sauron. Avant (jusqu'au 2026-10-14), on
// collait dans l'extension le jeton de session du site, qui expire au bout de
// 30 jours sans prévenir (« HTTP 401 Non authentifié » sur Cardmarket). Ces
// jetons-ci n'expirent pas et se révoquent d'un clic ; le jeton n'est affiché
// qu'une fois, à la création — à copier tout de suite dans les Options de
// l'extension.
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" }) : null);

const ExtensionTokens = () => {
  const [tokens, setTokens] = useState(null);
  const [fresh, setFresh] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);
  const freshRef = useRef(null);

  const load = async () => {
    try { setTokens(await fetchExtensionTokens()); setError(null); }
    catch (e) { setError("Impossible de lire les jetons."); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    setBusy(true); setCopied(false);
    try {
      const t = await createExtensionToken();
      setFresh(t);
      await load();
    } catch (e) { setError("Création impossible."); }
    finally { setBusy(false); }
  };

  const reveal = async (id) => {
    setCopied(false);
    try { setFresh(await revealExtensionToken(id)); setError(null); }
    catch (e) { setError("Impossible d'afficher ce jeton."); }
  };

  const copy = async () => {
    if (!fresh?.token) return;
    try {
      await navigator.clipboard.writeText(fresh.token);
      setCopied(true);
    } catch (e) {
      // Pas d'API presse-papiers (WebView ancienne) : on sélectionne le champ.
      freshRef.current?.select();
    }
  };

  const revoke = async (id) => {
    try {
      await revokeExtensionToken(id);
      if (fresh?.id === id) setFresh(null);
      await load();
    } catch (e) { setError("Révocation impossible."); }
  };

  return (
    <div className="mt-5">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-2">
        Œil de Sauron
      </h3>
      <p className="text-sm text-zinc-400 mb-3">
        Jeton à coller dans les Options de l'extension (clic sur son icône dans le
        navigateur). Il n'expire pas, contrairement au jeton de connexion du site
        (30 jours). Un seul peut servir sur tous tes navigateurs : « Afficher »
        le redonne quand tu veux. Révocable ici à tout moment.
      </p>
      <Button onClick={create} disabled={busy}
        className="bg-zinc-800 hover:bg-zinc-700 text-white border border-zinc-700">
        <KeyRound className="w-4 h-4 mr-2" />
        {busy ? "Génération…" : "Générer un jeton"}
      </Button>
      {fresh && (
        <div className="mt-3 rounded-lg border border-emerald-700/50 bg-emerald-900/15 p-3">
          <p className="text-xs text-emerald-300 mb-2">
            Colle-le dans les Options de l'extension. Tu pourras le réafficher ici.
          </p>
          <div className="flex items-center gap-2">
            <input ref={freshRef} readOnly value={fresh.token} onFocus={(e) => e.target.select()}
              className="flex-1 min-w-0 rounded-md border border-zinc-700 bg-zinc-950 px-2 py-1.5 text-xs font-mono text-zinc-200" />
            <Button size="sm" onClick={copy}
              className="bg-zinc-800 hover:bg-zinc-700 text-white border border-zinc-700 shrink-0">
              {copied ? <Check className="w-4 h-4 mr-1.5" /> : <Copy className="w-4 h-4 mr-1.5" />}
              {copied ? "Copié" : "Copier"}
            </Button>
          </div>
        </div>
      )}
      {error && <p className="text-sm text-red-400 mt-2">{error}</p>}
      {tokens && tokens.length > 0 && (
        <ul className="mt-3 divide-y divide-zinc-800 rounded-lg border border-zinc-800">
          {tokens.map(t => (
            <li key={t.id} className="flex items-center gap-3 px-3 py-2 text-sm">
              <KeyRound className="w-4 h-4 text-zinc-500 shrink-0" />
              <span className="flex-1 min-w-0">
                <span className="text-zinc-200">{t.name}</span>
                <span className="block text-xs text-zinc-500">
                  Créé le {fmtDate(t.created_at)}
                  {t.last_used_at ? ` · utilisé le ${fmtDate(t.last_used_at)}` : " · jamais utilisé"}
                </span>
              </span>
              <Button size="sm" onClick={() => reveal(t.id)}
                className="bg-zinc-800 hover:bg-zinc-700 text-white border border-zinc-700 shrink-0">
                <Copy className="w-4 h-4 mr-1.5" />
                Afficher
              </Button>
              <button onClick={() => revoke(t.id)} title="Révoquer ce jeton"
                className="text-zinc-500 hover:text-red-400 p-1 rounded">
                <Trash2 className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
      {tokens && tokens.length === 0 && !fresh && (
        <p className="text-xs text-zinc-500 mt-2">Aucun jeton pour l'instant.</p>
      )}
    </div>
  );
};

export const SettingsPanel = ({
  isOpen, onClose, blocs, prefs, onSavePrefs, onRefreshSeries, refreshingSeries,
  onOpenDebug, pendingWrites = 0, syncingWrites = false, onFlushWrites,
  onOpenAnomalies,
  rarityBalls, onSetRarityBall, onClearRarityBall,
}) => {
  const [refreshResult, setRefreshResult] = useState(null);
  const [expanded, setExpanded] = useState(() => new Set());
  const [backupMsg, setBackupMsg] = useState(null);
  const [backupBusy, setBackupBusy] = useState(false);
  const fileRef = useRef(null);

  // Mode hors ligne (étape 2) : vignettes + données synchronisées sur
  // l'appareil, cf. hooks/useOfflineSync.js.
  const offlineSync = useOfflineSync();
  // Étape 3 : images haute qualité en option (la fiche carte en grand),
  // instance séparée du même hook — progression et cache indépendants.
  const hqSync = useOfflineSync("high");
  const [clearingCache, setClearingCache] = useState(false);
  const [clearMsg, setClearMsg] = useState(null);
  const [testingStorage, setTestingStorage] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const runTestStorage = async () => {
    setTestingStorage(true); setTestResult(null);
    try {
      setTestResult(await testOfflineStorage());
    } catch (e) {
      setTestResult({ ok: false, step: "inattendu", error: e?.message || String(e) });
    } finally { setTestingStorage(false); }
  };

  const runClearOfflineCache = async () => {
    setClearingCache(true); setClearMsg(null);
    try {
      await clearOfflineCache();
      setClearMsg({ text: "Cache local vidé." });
    } catch (e) {
      setClearMsg({ error: true, text: "Échec : " + (e?.message || e) });
    } finally { setClearingCache(false); }
  };

  // Suivi Pokécardex : ce que Pokécardex a et pas nous (bloc le plus récent).
  // Le dernier résultat est mémorisé côté serveur : à l'ouverture du panneau
  // on relit ce résultat, Pokécardex n'est relu que sur « Vérifier maintenant »
  // (ou la toute première fois, s'il n'y a encore rien de mémorisé).
  const [pcxWatch, setPcxWatch] = useState(null);
  const [pcxBusy, setPcxBusy] = useState(false);
  const [pcxAdopting, setPcxAdopting] = useState(false);
  const [pcxMsg, setPcxMsg] = useState(null);
  const [pcxSelected, setPcxSelected] = useState(() => new Set());

  const runPcxCheck = async () => {
    setPcxBusy(true); setPcxMsg(null);
    try {
      setPcxWatch(await fetchPokecardexWatch(true));
    } catch (e) {
      setPcxMsg({ error: true, text: e?.response?.data?.detail || e?.message || String(e) });
    } finally { setPcxBusy(false); }
  };

  const pcxKey = (code, num) => `${code}:${num}`;
  const togglePcx = (code, num) => setPcxSelected(prev => {
    const next = new Set(prev);
    const k = pcxKey(code, num);
    if (next.has(k)) next.delete(k); else next.add(k);
    return next;
  });
  const setPcxAll = (w, on) => setPcxSelected(prev => {
    const next = new Set(prev);
    for (const m of w.missing || []) {
      if (on) next.add(pcxKey(w.code, m.num)); else next.delete(pcxKey(w.code, m.num));
    }
    return next;
  });
  const pcxCount = (w) => (w.missing || []).filter(m => pcxSelected.has(pcxKey(w.code, m.num))).length;

  const runPcxAdopt = async (w) => {
    const nums = (w.missing || []).map(m => m.num).filter(n => pcxSelected.has(pcxKey(w.code, n)));
    if (!nums.length) return;
    setPcxAdopting(true); setPcxMsg(null);
    try {
      const r = await adoptPokecardexCards(w.code, nums);
      const skipped = (r.skipped || []).length;
      setPcxMsg({ text: `${(r.added || []).length} carte(s) ajoutée(s) à ${w.name}${skipped ? `, ${skipped} ignorée(s)` : ""}.` });
      setPcxSelected(prev => { const next = new Set(prev); nums.forEach(n => next.delete(pcxKey(w.code, n))); return next; });
      // Le suivi a été invalidé côté serveur ; la liste des séries doit
      // refléter le nouveau nombre de cartes du set (vignette d'accueil).
      setPcxWatch(await fetchPokecardexWatch(false));
      onRefreshSeries?.();
    } catch (e) {
      setPcxMsg({ error: true, text: e?.response?.data?.detail || e?.message || String(e) });
    } finally { setPcxAdopting(false); }
  };

  useEffect(() => {
    if (!isOpen) return;
    fetchPokecardexWatch(false).then(setPcxWatch).catch(() => {});
  }, [isOpen]);

  const runExport = async () => {
    setBackupBusy(true); setBackupMsg(null);
    try {
      const n = await downloadBackup();
      setBackupMsg({ text: `Sauvegarde téléchargée (${n} éléments).` });
    } catch (e) {
      setBackupMsg({ error: true, text: "Échec de la sauvegarde : " + (e?.message || e) });
    } finally { setBackupBusy(false); }
  };

  // Restauration en mode « merge » : on ajoute et on met à jour, on n'efface
  // JAMAIS ce qui est déjà là. Écraser la collection en place serait
  // irréversible pour un gain douteux — un ré-import ne crée pas de doublon
  // (le backend identifie chaque carte par carte+variant).
  const runImport = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setBackupBusy(true); setBackupMsg(null);
    try {
      const data = JSON.parse(await file.text());
      const res = await uploadBackup(data, "merge");
      const n = Object.values(res.imported || {}).reduce((a, b) => a + b, 0);
      setBackupMsg({ text: `${n} éléments restaurés. Recharge la page pour les voir.` });
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || String(e);
      setBackupMsg({ error: true, text: "Échec de la restauration : " + detail });
    } finally { setBackupBusy(false); }
  };

  const hidden = new Set(prefs?.hidden || []);
  const hiddenSets = new Set(prefs?.hiddenSets || []);
  // Progression globale : on ne liste QUE les exclusions. Tout set visible et
  // non exclu compte — la barre est donc utile dès qu'on l'active, sans avoir
  // à cocher quoi que ce soit.
  const progressEnabled = !!prefs?.progressEnabled;
  const progressExcluded = new Set(prefs?.progressExcluded || []);
  const blocKeys = blocs.map(b => b.key);

  const persist = (patch) => onSavePrefs({ ...prefs, ...patch }).catch(() => {});

  const toggleExpanded = (key) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // ── Blocs ──
  const toggleBloc = (key) => {
    const next = hidden.has(key)
      ? (prefs.hidden || []).filter(k => k !== key)
      : [...(prefs.hidden || []), key];
    persist({ hidden: next, order: blocKeys });
  };

  const moveBloc = (key, delta) => {
    const i = blocKeys.indexOf(key);
    const j = i + delta;
    if (i < 0 || j < 0 || j >= blocKeys.length) return;
    const next = [...blocKeys];
    [next[i], next[j]] = [next[j], next[i]];
    persist({ order: next });
  };

  // ── Sets d'un bloc ──
  // L'ordre enregistré est global (une seule liste d'ids) ; on n'y réécrit que
  // les sets du bloc manipulé, les autres blocs gardent leur ordre existant.
  const setOrderFor = (bloc) => bloc.sets.map(s => s.id);

  const mergedSetOrder = (bloc, newIds) => {
    const previous = (prefs.setOrder || []).filter(id => !bloc.sets.some(s => s.id === id));
    return [...previous, ...newIds];
  };

  const toggleSet = (setId) => {
    const next = hiddenSets.has(setId)
      ? (prefs.hiddenSets || []).filter(id => id !== setId)
      : [...(prefs.hiddenSets || []), setId];
    persist({ hiddenSets: next });
  };

  const moveSet = (bloc, setId, delta) => {
    const ids = setOrderFor(bloc);
    const i = ids.indexOf(setId);
    const j = i + delta;
    if (i < 0 || j < 0 || j >= ids.length) return;
    const next = [...ids];
    [next[i], next[j]] = [next[j], next[i]];
    persist({ setOrder: mergedSetOrder(bloc, next) });
  };

  // ── Périmètre de la barre de progression globale ──
  // Deux réglages DISTINCTS par set : l'œil (visible dans la sidebar) et le
  // graphique (compté dans la progression). On peut donc garder une collection
  // sous les yeux sans qu'elle plombe le pourcentage — et un set masqué, lui,
  // n'est jamais compté (règle appliquée côté App : visible ET non exclu).
  const toggleProgressSet = (setId) => {
    const next = progressExcluded.has(setId)
      ? (prefs.progressExcluded || []).filter(id => id !== setId)
      : [...(prefs.progressExcluded || []), setId];
    persist({ progressExcluded: next });
  };

  const toggleProgressBloc = (bloc) => {
    const ids = bloc.sets.map(s => s.id);
    const allOut = ids.every(id => progressExcluded.has(id));
    const next = new Set(progressExcluded);
    ids.forEach(id => allOut ? next.delete(id) : next.add(id));
    persist({ progressExcluded: [...next] });
  };

  // Ne remet à zéro que l'organisation de l'affichage : le thème choisi n'a
  // rien à voir avec les blocs masqués, le perdre au passage serait une surprise.
  const resetAll = () => persist({
    hidden: [], order: [], hiddenSets: [], setOrder: [],
    progressEnabled: false, progressExcluded: [],
  });

  const doRefresh = async () => {
    setRefreshResult(null);
    try {
      const data = await onRefreshSeries();
      setRefreshResult({ ok: true, text: `${data.serie_count} séries, ${data.set_count} sets à jour.` });
    } catch (err) {
      // Le plus souvent : TCGdex injoignable. On le dit au lieu de ne rien faire.
      setRefreshResult({ ok: false, text: "Échec — l'API TCGdex n'a pas répondu. Réessayez dans un moment." });
    }
  };

  const iconBtn = "text-zinc-500 hover:text-white disabled:opacity-25 disabled:hover:text-zinc-500";

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-zinc-800 max-w-xl max-h-[88vh] overflow-hidden p-0">
        <ScrollArea className="max-h-[88vh]">
          <div className="p-6">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-white">Réglages</DialogTitle>
            </DialogHeader>

            {/* ── Thème ── */}
            <div className="mt-5">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-2">
                Thème
              </h3>
              <div className="grid grid-cols-3 gap-2">
                {THEMES.map(t => {
                  const active = (prefs?.theme || "dark") === t.id;
                  return (
                    <button key={t.id} onClick={() => persist({ theme: t.id })}
                      title={t.hint}
                      className={`rounded-lg border p-2 text-left transition-colors ${
                        active ? "border-red-500 bg-zinc-800" : "border-zinc-700 hover:bg-zinc-800"}`}>
                      <span className="flex gap-1 mb-1.5">
                        {t.swatch.map(c => (
                          <span key={c} className="w-4 h-4 rounded-full border border-zinc-700"
                            style={{ background: c }} />
                        ))}
                      </span>
                      <span className="block text-sm text-white">{t.label}</span>
                      <span className="block text-[11px] text-zinc-500 leading-tight">{t.hint}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* ── Avatar (barre du haut) ── */}
            <div className="mt-5">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-2">
                Avatar
              </h3>
              <AvatarPicker value={prefs?.avatar ?? null} onChange={(dexId) => persist({ avatar: dexId })} />
            </div>

            {/* ── Jetons Œil de Sauron ── */}
            <ExtensionTokens />

            {/* ── Mise à jour des données ── */}
            <div className="mt-5">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-2">
                Séries et sets
              </h3>
              <p className="text-sm text-zinc-400 mb-3">
                Va rechercher la liste des séries et des sets chez TCGdex tout de suite,
                sans attendre la mise à jour automatique (toutes les 24 h). À utiliser
                quand un nouveau set vient de sortir.
              </p>
              <Button onClick={doRefresh} disabled={refreshingSeries}
                className="bg-zinc-800 hover:bg-zinc-700 text-white border border-zinc-700">
                <RefreshCw className={`w-4 h-4 mr-2 ${refreshingSeries ? "animate-spin" : ""}`} />
                {refreshingSeries ? "Mise à jour…" : "Mettre à jour maintenant"}
              </Button>
              {refreshResult && (
                <p className={`text-sm mt-2 flex items-center gap-1.5 ${refreshResult.ok ? "text-emerald-400" : "text-red-400"}`}>
                  {refreshResult.ok && <Check className="w-4 h-4" />}
                  {refreshResult.text}
                </p>
              )}
            </div>

            {/* ── Suivi Pokécardex ── */}
            <div className="mt-7">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-2">
                Suivi Pokécardex
              </h3>
              <p className="text-sm text-zinc-400 mb-3">
                Compare le bloc le plus récent de Pokécardex avec nos collections :
                nouvelles séries à créer, et cartes qu'ils ont en plus (promos en
                cours, énergies…). Les cartes cochées rejoignent le catalogue avec le
                scan Pokécardex comme image, en attendant que TCGdex les rattrape.
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <Button onClick={runPcxCheck} disabled={pcxBusy}
                  className="bg-zinc-800 hover:bg-zinc-700 text-white border border-zinc-700">
                  <RefreshCw className={`w-4 h-4 mr-2 ${pcxBusy ? "animate-spin" : ""}`} />
                  {pcxBusy ? "Vérification…" : "Vérifier maintenant"}
                </Button>
                {pcxWatch?.checked_at && (
                  <span className="text-xs text-zinc-500">
                    Dernière vérification : {new Date(pcxWatch.checked_at).toLocaleString("fr-FR")}
                  </span>
                )}
              </div>
              {pcxMsg && (
                <p className={`text-sm mt-2 flex items-center gap-1.5 ${pcxMsg.error ? "text-red-400" : "text-emerald-400"}`}>
                  {!pcxMsg.error && <Check className="w-4 h-4" />}
                  {pcxMsg.text}
                </p>
              )}
              {pcxWatch && !pcxWatch.ok && (
                <p className="text-sm mt-2 text-red-400">
                  Pokécardex est injoignable pour le moment (ou a changé sa page) — réessaie plus tard.
                </p>
              )}
              {pcxWatch?.ok && (
                <div className="mt-3 space-y-3">
                  <div>
                    <p className="text-xs uppercase tracking-wide text-zinc-500 mb-1">
                      Nouvelles séries chez Pokécardex
                    </p>
                    {(pcxWatch.new_series || []).length === 0 ? (
                      <p className="text-sm text-zinc-500">Aucune — tout ce qu'ils listent existe chez nous.</p>
                    ) : (
                      <ul className="text-sm space-y-0.5">
                        {pcxWatch.new_series.map(s => (
                          <li key={s.code} className="text-amber-300">
                            <span className="font-mono text-xs text-zinc-400 mr-2">{s.code}</span>
                            {s.name} <span className="text-zinc-500">({s.block})</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-zinc-500 mb-1">
                      Bloc suivi : {pcxWatch.block}
                    </p>
                    {(pcxWatch.watched || []).map(w => (
                      <div key={w.code} className="rounded-lg border border-zinc-700 p-2 mb-2">
                        <div className="flex items-center justify-between gap-2 text-sm">
                          <span className="text-white truncate">
                            {w.name || w.set_id} <span className="text-zinc-500 font-mono text-xs">{w.code}</span>
                          </span>
                          {w.error ? (
                            <span className="text-red-400 text-xs shrink-0">indisponible</span>
                          ) : (
                            <span className={`shrink-0 ${(w.missing || []).length ? "text-amber-400" : "text-emerald-400"}`}>
                              {w.our_total} / {w.pokecardex_total}
                            </span>
                          )}
                        </div>
                        {(w.missing || []).length > 0 && (
                          <div className="mt-2">
                            <div className="flex flex-wrap items-center gap-2 mb-1">
                              <button type="button" onClick={() => setPcxAll(w, true)}
                                className="text-xs text-zinc-400 hover:text-white">Tout cocher</button>
                              <button type="button" onClick={() => setPcxAll(w, false)}
                                className="text-xs text-zinc-400 hover:text-white">Tout décocher</button>
                              <Button onClick={() => runPcxAdopt(w)} disabled={pcxAdopting || !pcxCount(w)}
                                className="h-7 px-3 text-xs bg-red-600 hover:bg-red-700 text-white disabled:opacity-40">
                                Ajouter {pcxCount(w)} carte{pcxCount(w) > 1 ? "s" : ""}
                              </Button>
                            </div>
                            <ul className="max-h-52 overflow-y-auto text-sm pr-1">
                              {w.missing.map(m => (
                                <li key={m.num}>
                                  <label className="flex items-center gap-2 py-0.5 cursor-pointer">
                                    <input type="checkbox"
                                      checked={pcxSelected.has(`${w.code}:${m.num}`)}
                                      onChange={() => togglePcx(w.code, m.num)} />
                                    <span className="font-mono text-xs text-zinc-500 w-14 shrink-0">{m.num}</span>
                                    <span className="text-white truncate">{m.name}</span>
                                    {m.rarity && <span className="text-xs text-zinc-500 shrink-0">{m.rarity}</span>}
                                  </label>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* ── Mode hors ligne ── */}
            <div className="mt-7">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-2">
                Mode hors ligne
              </h3>
              <p className="text-sm text-zinc-400 mb-3">
                Télécharge les vignettes et les données de chaque collection sur cet
                appareil, pour pouvoir consulter ta collection sans réseau. Peut être
                interrompu et repris à tout moment — rien n'est retéléchargé deux fois.
              </p>
              {offlineSync.totalSets > 0 && (
                <div className="mb-3">
                  <div className="flex items-center justify-between text-xs text-zinc-500 mb-1">
                    <span>{offlineSync.syncedSets} / {offlineSync.totalSets} collections</span>
                    <span>{offlineSync.cachedImages} / {offlineSync.totalImages} images</span>
                  </div>
                  <Progress
                    value={offlineSync.totalSets ? (offlineSync.syncedSets / offlineSync.totalSets) * 100 : 0}
                    className="h-1.5" />
                  {offlineSync.currentSetName && (
                    <p className="text-xs text-zinc-600 mt-1">En cours : {offlineSync.currentSetName}</p>
                  )}
                </div>
              )}
              <div className="flex flex-wrap items-center gap-2">
                <Button onClick={offlineSync.running ? offlineSync.stop : offlineSync.start}
                  className="bg-zinc-800 hover:bg-zinc-700 text-white border border-zinc-700">
                  <RefreshCw className={`w-4 h-4 mr-2 ${offlineSync.running ? "animate-spin" : ""}`} />
                  {offlineSync.running ? "Interrompre" : "Synchroniser maintenant"}
                </Button>
                <Button onClick={runClearOfflineCache} disabled={offlineSync.running || clearingCache}
                  variant="ghost" className="text-zinc-400 hover:text-white disabled:opacity-40">
                  Vider le cache local
                </Button>
                <Button onClick={runTestStorage} disabled={testingStorage}
                  variant="ghost" className="text-zinc-400 hover:text-white disabled:opacity-40">
                  Tester le stockage
                </Button>
              </div>
              {offlineSync.error && <p className="text-sm mt-2 text-red-400">{offlineSync.error}</p>}
              {clearMsg && (
                <p className={`text-sm mt-2 ${clearMsg.error ? "text-red-400" : "text-emerald-400"}`}>
                  {clearMsg.text}
                </p>
              )}
              {testResult && (
                <p className={`text-sm mt-2 ${testResult.ok ? "text-emerald-400" : "text-red-400"}`}>
                  {testResult.ok
                    ? `Stockage OK (${testResult.platform}) — ${testResult.sets} collection(s), ${testResult.images} image(s) en cache.`
                    : `Échec à l'étape "${testResult.step}" : ${testResult.error}`}
                </p>
              )}

              {pendingWrites > 0 && (
                <div className="mt-4 pt-4 border-t border-zinc-800">
                  <p className="text-sm text-amber-400 mb-2">
                    {pendingWrites} modification{pendingWrites > 1 ? "s" : ""} de collection en attente
                    de synchronisation{syncingWrites ? " (en cours…)" : ""}.
                  </p>
                  <Button onClick={onFlushWrites} disabled={syncingWrites}
                    className="bg-zinc-800 hover:bg-zinc-700 text-white border border-zinc-700 disabled:opacity-40">
                    <RefreshCw className={`w-4 h-4 mr-2 ${syncingWrites ? "animate-spin" : ""}`} />
                    Synchroniser maintenant
                  </Button>
                </div>
              )}
            </div>

            {/* ── Images haute qualité (étape 3, à la demande) ── */}
            <div className="mt-7">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-2">
                Images haute qualité
              </h3>
              <p className="text-sm text-zinc-400 mb-3">
                Télécharge en plus la version haute qualité de chaque carte (celle de la
                fiche en grand). La qualité standard ci-dessus reste celle synchronisée par
                défaut ; ceci est optionnel et peut représenter plusieurs Go — une connexion
                Wi-Fi est recommandée.
              </p>
              {hqSync.totalSets > 0 && (
                <div className="mb-3">
                  <div className="flex items-center justify-between text-xs text-zinc-500 mb-1">
                    <span>{hqSync.syncedSets} / {hqSync.totalSets} collections</span>
                    <span>{hqSync.cachedImages} / {hqSync.totalImages} images HQ</span>
                  </div>
                  <Progress
                    value={hqSync.totalSets ? (hqSync.syncedSets / hqSync.totalSets) * 100 : 0}
                    className="h-1.5" />
                  {hqSync.currentSetName && (
                    <p className="text-xs text-zinc-600 mt-1">En cours : {hqSync.currentSetName}</p>
                  )}
                </div>
              )}
              <Button onClick={hqSync.running ? hqSync.stop : hqSync.start}
                className="bg-zinc-800 hover:bg-zinc-700 text-white border border-zinc-700">
                <RefreshCw className={`w-4 h-4 mr-2 ${hqSync.running ? "animate-spin" : ""}`} />
                {hqSync.running ? "Interrompre" : "Télécharger la haute qualité"}
              </Button>
              {hqSync.error && <p className="text-sm mt-2 text-red-400">{hqSync.error}</p>}
            </div>

            {/* ── Diagnostic ── */}
            <div className="mt-7">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-2">
                Diagnostic
              </h3>
              <p className="text-sm text-zinc-400 mb-3">
                Liste les cartes mal renseignées (sans image, variants inconnus ou douteux)
                pour les repérer et les corriger.
              </p>
              <Button onClick={onOpenDebug}
                className="bg-zinc-800 hover:bg-zinc-700 text-white border border-zinc-700">
                <Bug className="w-4 h-4 mr-2" />Voir les cartes à problème
              </Button>
            </div>

            {/* ── Anomalies de prix ── */}
            <div className="mt-7">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-2">
                Anomalies de prix
              </h3>
              <p className="text-sm text-zinc-400 mb-3">
                Cartes anormalement chères ou anormalement bon marché pour leur
                rareté (comparées aux autres cartes de même rareté dans la même
                extension).
              </p>
              <Button onClick={onOpenAnomalies}
                className="bg-zinc-800 hover:bg-zinc-700 text-white border border-zinc-700">
                <TrendingUp className="w-4 h-4 mr-2" />Voir les anomalies
              </Button>
            </div>

            {/* ── Balls de rareté ── */}
            <div className="mt-7">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-2">
                Balls de rareté
              </h3>
              <p className="text-sm text-zinc-400 mb-3">
                Quand une carte n'a qu'une seule variante « Holo », la Poké Ball
                affichée suit sa rareté plutôt qu'une Hyper Ball générique.
                Modifiable ici (réservé aux admins).
              </p>
              <RarityBallSettings
                rarityBalls={rarityBalls}
                onSetRarityBall={onSetRarityBall}
                onClearRarityBall={onClearRarityBall}
              />
            </div>

            {/* ── Sauvegarde ── */}
            <div className="mt-7">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-2">
                Sauvegarde
              </h3>
              <p className="text-sm text-zinc-400 mb-3">
                Enregistre ta collection, tes listes, tes variantes perso et tes réglages
                dans un fichier. Les images et les données des cartes n'y sont pas : elles
                se retéléchargent toutes seules.
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <Button onClick={runExport} disabled={backupBusy}
                  className="bg-emerald-600 hover:bg-emerald-500 text-white">
                  <Download className="w-4 h-4 mr-2" />Télécharger la sauvegarde
                </Button>
                <Button onClick={() => fileRef.current?.click()} disabled={backupBusy}
                  className="bg-zinc-800 hover:bg-zinc-700 text-white border border-zinc-700">
                  <Upload className="w-4 h-4 mr-2" />Restaurer un fichier
                </Button>
                {/* Champ masqué : le bouton stylé ci-dessus le déclenche. */}
                <input ref={fileRef} type="file" accept="application/json,.json"
                  className="hidden" onChange={runImport} />
              </div>
              {backupMsg && (
                <p className={`text-sm mt-2 ${backupMsg.error ? "text-red-400" : "text-emerald-400"}`}>
                  {backupMsg.text}
                </p>
              )}
            </div>

            {/* ── Scanner ── */}
            <ScannerSettings isOpen={isOpen} />

            {/* ── Progression globale ── */}
            <div className="mt-7">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-2">
                Progression globale
              </h3>
              <p className="text-sm text-zinc-400 mb-3">
                Une barre unique sur l'accueil, qui additionne toutes les collections
                affichées. Les collections masquées n'y comptent jamais ; pour en
                écarter une tout en la gardant visible, utilisez l'icône
                <BarChart3 className="w-3.5 h-3.5 inline mx-1 text-sky-400" />
                dans la liste ci-dessous.
              </p>
              <Button onClick={() => persist({ progressEnabled: !progressEnabled })}
                className={progressEnabled
                  ? "bg-sky-600 hover:bg-sky-500 text-white"
                  : "bg-zinc-800 hover:bg-zinc-700 text-white border border-zinc-700"}>
                <BarChart3 className="w-4 h-4 mr-2" />
                {progressEnabled ? "Barre affichée" : "Afficher la barre"}
              </Button>

              <p className="text-sm text-zinc-400 mt-4 mb-3">
                Le Pokédex peut aussi s'afficher sur l'accueil : une barre par région,
                pour voir d'un coup d'œil combien d'espèces sont couvertes.
              </p>
              <Button onClick={() => persist({ pokedexHome: !prefs?.pokedexHome })}
                className={prefs?.pokedexHome
                  ? "bg-emerald-600 hover:bg-emerald-500 text-white"
                  : "bg-zinc-800 hover:bg-zinc-700 text-white border border-zinc-700"}>
                <BookOpen className="w-4 h-4 mr-2" />
                {prefs?.pokedexHome ? "Pokédex affiché sur l'accueil" : "Afficher le Pokédex sur l'accueil"}
              </Button>
            </div>

            {/* ── Affichage : blocs et sets ── */}
            <div className="mt-7">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
                  Affichage
                </h3>
                <button onClick={resetAll}
                  className="text-xs text-zinc-500 hover:text-zinc-300 flex items-center gap-1">
                  <RotateCcw className="w-3 h-3" />Tout réinitialiser
                </button>
              </div>
              <p className="text-sm text-zinc-400 mb-1">
                L'œil masque, les flèches réordonnent. Le chevron déplie un bloc pour
                faire pareil sur ses collections une par une.
              </p>
              <p className="text-sm text-zinc-400 mb-3">
                <BarChart3 className="w-3.5 h-3.5 inline mr-1 text-sky-400" />
                allumé = compté dans la barre de progression globale, éteint = ignoré.
              </p>

              <div className="space-y-1">
                {blocs.map((bloc, i) => {
                  const isHidden = hidden.has(bloc.key);
                  const open = expanded.has(bloc.key);
                  const inProgress = bloc.sets.filter(s => !progressExcluded.has(s.id)).length;
                  return (
                    <div key={bloc.key}>
                      <div className="flex items-center gap-2 bg-zinc-800/50 rounded px-2 py-2">
                        <button onClick={() => toggleExpanded(bloc.key)} className={iconBtn}
                          title="Voir les collections de ce bloc">
                          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        </button>
                        <button onClick={() => toggleBloc(bloc.key)}
                          title={isHidden ? "Afficher ce bloc" : "Masquer ce bloc"}
                          className={isHidden ? "text-zinc-600 hover:text-zinc-300" : "text-emerald-400 hover:text-emerald-300"}>
                          {isHidden ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                        <span className={`flex-1 text-sm ${isHidden ? "text-zinc-600 line-through" : "text-zinc-200"}`}>
                          {bloc.label}
                        </span>
                        <button onClick={() => toggleProgressBloc(bloc)}
                          title={inProgress > 0
                            ? "Retirer ce bloc de la progression globale"
                            : "Compter ce bloc dans la progression globale"}
                          className={inProgress === bloc.sets.length && inProgress > 0
                            ? "text-sky-400" : inProgress > 0 ? "text-sky-700" : "text-zinc-600 hover:text-zinc-400"}>
                          <BarChart3 className="w-4 h-4" />
                        </button>
                        <span className="text-xs text-zinc-600 w-8 text-right">{bloc.sets.length}</span>
                        <button onClick={() => moveBloc(bloc.key, -1)} disabled={i === 0}
                          title="Monter" className={iconBtn}><ArrowUp className="w-4 h-4" /></button>
                        <button onClick={() => moveBloc(bloc.key, 1)} disabled={i === blocs.length - 1}
                          title="Descendre" className={iconBtn}><ArrowDown className="w-4 h-4" /></button>
                      </div>

                      {open && (
                        <div className="ml-6 mt-1 mb-2 space-y-0.5 border-l border-zinc-800 pl-2">
                          {bloc.sets.map((set, j) => {
                            const setHidden = hiddenSets.has(set.id);
                            return (
                              <div key={set.id} className="flex items-center gap-2 px-2 py-1">
                                <button onClick={() => toggleSet(set.id)}
                                  title={setHidden ? "Afficher cette collection" : "Masquer cette collection"}
                                  className={setHidden ? "text-zinc-600 hover:text-zinc-300" : "text-emerald-400/70 hover:text-emerald-300"}>
                                  {setHidden ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                                </button>
                                <span className={`flex-1 text-xs ${setHidden ? "text-zinc-600 line-through" : "text-zinc-400"}`}>
                                  {set.name}
                                  {set.lang === "en" && (
                                    <span className="ml-1.5 text-[10px] text-amber-500/80" title="Collection disponible uniquement en anglais">EN</span>
                                  )}
                                </span>
                                <button onClick={() => toggleProgressSet(set.id)}
                                  title={progressExcluded.has(set.id)
                                    ? "Compter cette collection dans la progression globale"
                                    : "Retirer cette collection de la progression globale"}
                                  className={progressExcluded.has(set.id)
                                    ? "text-zinc-600 hover:text-zinc-400" : "text-sky-400/80 hover:text-sky-300"}>
                                  <BarChart3 className="w-3.5 h-3.5" />
                                </button>
                                <button onClick={() => moveSet(bloc, set.id, -1)} disabled={j === 0}
                                  title="Monter" className={iconBtn}><ArrowUp className="w-3.5 h-3.5" /></button>
                                <button onClick={() => moveSet(bloc, set.id, 1)} disabled={j === bloc.sets.length - 1}
                                  title="Descendre" className={iconBtn}><ArrowDown className="w-3.5 h-3.5" /></button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
